import { useState, useEffect, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import api from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import ProjectStageReviewCard from '../../components/projectlab/ProjectStageReviewCard'

const STATUS_TONE = {
  submitted: 'text-cyan-400',
  needs_revision: 'text-amber-400',
  approved: 'text-emerald-400',
  completed: 'text-emerald-400',
}

function fmt(dt) {
  if (!dt) return '—'
  return new Date(dt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/**
 * MentorReviewWorkspace — authoritative review surface for one assigned
 * participant enrollment. The mentor inspects the current stage's authored
 * brief against the participant's latest submission and records a decision
 * (approve / request revision). The backend is authoritative for all state
 * transitions; this UI only requests a decision.
 */
export default function MentorReviewWorkspace() {
  const { enrollmentId } = useParams()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [feedback, setFeedback] = useState('')
  const [submitting, setSubmitting] = useState(null)
  const [notice, setNotice] = useState(null)

  const load = useCallback(() => {
    api.get(`/project-lab/mentor/enrollments/${enrollmentId}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setError(e.response?.data?.error || 'Failed to load review workspace')
        setData(null)
      })
  }, [enrollmentId])

  useEffect(() => { load() }, [load])

  const decide = async (action) => {
    if (!data) return
    setSubmitting(action)
    setNotice(null)
    try {
      const r = await api.post(
        `/project-lab/mentor/enrollments/${enrollmentId}/stages/${data.enrollment.current_stage_index}/review`,
        { action, feedback: feedback.trim() || undefined }
      )
      setNotice({ tone: 'ok', text: r.data.message })
      setFeedback('')
      load()
    } catch (e) {
      setNotice({ tone: 'err', text: e.response?.data?.error || 'Review action failed' })
      load()
    } finally {
      setSubmitting(null)
    }
  }

  if (error) {
    return (
      <div className="max-w-3xl space-y-4">
        <Link to="/mentor/participants" className="text-xs text-cinema-gold hover:underline">← Participants</Link>
        <p className="text-sm text-red-400">{error}</p>
      </div>
    )
  }
  if (!data) return <div className="flex justify-center py-16"><LoadingSpinner /></div>

  const { enrollment, participant, project, stages, submissions, reviews } = data
  const currentStage = stages.find((s) => s.stage_index === enrollment.current_stage_index)
  const stageSubmissions = currentStage
    ? submissions.filter((s) => s.stage_index === currentStage.stage_index)
    : []
  const latestSubmission = stageSubmissions[0] || null
  const reviewable = enrollment.status === 'active' && currentStage?.status === 'submitted'

  return (
    <div className="max-w-4xl space-y-10">
      {/* Header */}
      <div className="space-y-2">
        <Link to="/mentor/participants" className="text-xs text-cinema-gold hover:underline">← Participants</Link>
        <p className="text-[10px] uppercase tracking-widest text-cinema-muted font-mono">Project Lab · Review</p>
        <h1 className="font-display text-2xl text-white">{project.title}</h1>
        <p className="text-sm text-cinema-soft">
          {participant.first_name} {participant.last_name}
          <span className="text-cinema-muted"> · {participant.email}</span>
        </p>
        <p className="text-xs text-cinema-muted uppercase tracking-wider">
          Stage {enrollment.current_stage_index} of {stages.length} · {enrollment.status}
        </p>
      </div>

      {notice && (
        <p className={`text-sm border-l-2 pl-3 py-1 ${notice.tone === 'ok' ? 'border-emerald-500 text-emerald-300' : 'border-red-500 text-red-300'}`}>
          {notice.text}
        </p>
      )}

      {/* Stage progression */}
      <section>
        <h2 className="text-xs uppercase tracking-widest text-cinema-muted font-mono pb-3 border-b border-white/10">
          Stage Progression
        </h2>
        <div className="divide-y divide-white/5">
          {stages.map((s) => (
            <div key={s.stage_index} className="flex items-baseline gap-4 py-2.5">
              <span className="text-xs font-mono text-cinema-muted w-8 shrink-0">
                {String(s.stage_index).padStart(2, '0')}
              </span>
              <p className={`text-sm min-w-0 ${s.stage_index === enrollment.current_stage_index ? 'text-white' : 'text-cinema-soft'}`}>
                {s.title}
              </p>
              <span className="ml-auto text-[10px] uppercase tracking-wider text-cinema-muted shrink-0">
                {s.completion_mode}
              </span>
              <span className={`text-[10px] uppercase tracking-wider font-mono w-24 text-right shrink-0 ${STATUS_TONE[s.status] || 'text-cinema-muted'}`}>
                {s.status.replace(/_/g, ' ')}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Current stage brief */}
      {currentStage && (
        <section className="space-y-5">
          <h2 className="text-xs uppercase tracking-widest text-cinema-muted font-mono pb-3 border-b border-white/10">
            Stage {String(currentStage.stage_index).padStart(2, '0')} — {currentStage.title}
          </h2>
          {currentStage.objective && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-cinema-muted font-mono mb-1.5">Objective</p>
              <p className="text-sm text-cinema-soft">{currentStage.objective}</p>
            </div>
          )}
          {currentStage.requirements?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-cinema-muted font-mono mb-1.5">Requirements</p>
              <ul className="text-sm text-cinema-soft space-y-1 list-disc list-inside">
                {currentStage.requirements.map((r, i) => <li key={i}>{typeof r === 'string' ? r : r.text || r.title}</li>)}
              </ul>
            </div>
          )}
          {currentStage.acceptance_criteria?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-cinema-muted font-mono mb-1.5">Acceptance Criteria</p>
              <ul className="text-sm text-cinema-soft space-y-1 list-disc list-inside">
                {currentStage.acceptance_criteria.map((c, i) => <li key={i}>{typeof c === 'string' ? c : c.text || c.title}</li>)}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Submission */}
      <section className="space-y-4">
        <h2 className="text-xs uppercase tracking-widest text-cinema-muted font-mono pb-3 border-b border-white/10">
          Submission
        </h2>
        {!latestSubmission ? (
          <p className="text-sm text-cinema-muted">
            {currentStage?.completion_mode === 'submission'
              ? 'No submission yet for this stage.'
              : 'This stage does not require a file submission.'}
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-baseline gap-4">
              <p className="text-sm text-white">Version {latestSubmission.version}</p>
              <p className="text-xs text-cinema-muted">Submitted {fmt(latestSubmission.created_at)}</p>
              <span className={`text-[10px] uppercase tracking-wider font-mono ${STATUS_TONE[latestSubmission.status] || 'text-cinema-muted'}`}>
                {latestSubmission.status?.replace(/_/g, ' ')}
              </span>
            </div>
            {latestSubmission.participant_notes && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-cinema-muted font-mono mb-1.5">Participant Notes</p>
                <p className="text-sm text-cinema-soft whitespace-pre-wrap">{latestSubmission.participant_notes}</p>
              </div>
            )}
            {latestSubmission.files?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-cinema-muted font-mono mb-1.5">Files</p>
                <div className="divide-y divide-white/5 border-t border-white/10">
                  {latestSubmission.files.map((f) => (
                    <a
                      key={f.id}
                      href={`/api/project-lab/files/${f.id}/download`}
                      className="flex items-baseline justify-between gap-4 py-2.5 group"
                    >
                      <span className="text-sm text-cinema-soft group-hover:text-cinema-gold transition-colors truncate">
                        {f.original_filename}
                      </span>
                      <span className="text-xs text-cinema-muted shrink-0">
                        {f.is_final_deliverable ? 'final deliverable · ' : ''}
                        {f.size_bytes ? `${(f.size_bytes / 1024).toFixed(0)} KB` : ''}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
            {stageSubmissions.length > 1 && (
              <p className="text-xs text-cinema-muted">
                {stageSubmissions.length - 1} earlier version{stageSubmissions.length > 2 ? 's' : ''} preserved (v{stageSubmissions[stageSubmissions.length - 1].version}–v{latestSubmission.version}).
              </p>
            )}
          </div>
        )}
      </section>

      {/* Review history */}
      {reviews.length > 0 && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-cinema-muted font-mono pb-3 border-b border-white/10">
            Review History
          </h2>
          <div className="divide-y divide-white/5">
            {reviews.map((r) => (
              <div key={r.id} className="py-3 space-y-1">
                <div className="flex items-baseline gap-3">
                  <span className={`text-[10px] uppercase tracking-wider font-mono ${r.decision === 'approved' ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {r.decision.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs text-cinema-muted">
                    Stage {r.stage_index}{r.submission_version ? ` · v${r.submission_version}` : ''} · {fmt(r.created_at)}
                  </span>
                </div>
                {r.feedback && <p className="text-sm text-cinema-soft">{r.feedback}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* AI-013 Project Lab Milestone Review & Delivery Assistant */}
      <ProjectStageReviewCard
        enrollmentId={enrollment.id}
        stageIndex={enrollment.current_stage_index}
        apiPrefix="/project-lab/mentor/enrollments"
        onApplyDraft={(draft) => setFeedback(draft)}
      />

      {/* Review actions */}
      <section className="space-y-4 border-t border-white/10 pt-6">
        <h2 className="text-xs uppercase tracking-widest text-cinema-muted font-mono">Review Decision</h2>
        {!reviewable ? (
          <p className="text-sm text-cinema-muted">
            {enrollment.status !== 'active'
              ? 'This enrollment is completed — no further review actions.'
              : currentStage?.status === 'needs_revision'
                ? 'Awaiting participant resubmission.'
                : 'This stage is not awaiting review.'}
          </p>
        ) : (
          <>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={4}
              placeholder="Review feedback — required when requesting revision"
              className="w-full bg-transparent border border-white/15 rounded-none px-3 py-2.5 text-sm text-white placeholder:text-cinema-muted focus:border-cinema-gold focus:outline-none"
            />
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => decide('request_revision')}
                disabled={submitting !== null}
                className="px-5 py-2.5 border border-amber-500/60 text-amber-300 text-xs uppercase tracking-widest font-mono rounded-none hover:bg-amber-500/10 disabled:opacity-40 transition-colors"
              >
                {submitting === 'request_revision' ? 'Requesting…' : 'Request Revision'}
              </button>
              <button
                onClick={() => decide('approve')}
                disabled={submitting !== null}
                className="px-5 py-2.5 bg-cinema-gold text-black text-xs uppercase tracking-widest font-mono rounded-none hover:bg-cinema-gold/90 disabled:opacity-40 transition-colors"
              >
                {submitting === 'approve' ? 'Approving…' : 'Approve Stage'}
              </button>
            </div>
            <p className="text-[10px] text-cinema-muted">
              Feedback is required for revision requests. The database validates the stage state — your decision is applied atomically.
            </p>
          </>
        )}
      </section>
    </div>
  )
}
